import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Объединяет классы Tailwind CSS с поддержкой условий
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * csvEscape экранирует значение для безопасного CSV-экспорта.
 * 1. Formula injection: префиксы =, +, -, @ нейтрализуются добавлением одинарной кавычки.
 * 2. RFC 4180: поля с запятыми, кавычками или переносами строк оборачиваются в двойные кавычки.
 * 3. Двойные кавычки внутри поля удваиваются.
 */
export function csvEscape(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const isNumber = typeof value === 'number';
  const s = String(value);
  // Formula injection protection: нейтрализуем опасные префиксы только для строк.
  // Numbers не могут быть formula injection (отрицательные числа начинаются с -).
  const dangerous = /^[=+\-@]/;
  let safe = s;
  if (!isNumber && dangerous.test(safe)) {
    safe = "'" + safe;
  }
  // RFC 4180 escaping: если есть запятые, кавычки или переносы — оборачиваем.
  if (/[",\n\r]/.test(safe)) {
    return '"' + safe.replace(/"/g, '""') + '"';
  }
  return safe;
}

/**
 * Форматирует дату в русском формате
 */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Форматирует дату и время в русском формате
 */
export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Возвращает человекочитаемое название роли пользователя
 */
export function getRoleLabel(role: string): string {
  switch (role) {
    case 'admin': return 'Администратор';
    case 'manager': return 'Менеджер';
    case 'operator': return 'Оператор';
    default: return role;
  }
}

/**
 * Форматирует цену в рублях
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
  }).format(price);
}

/**
 * Реестр валют: код → количество знаков после запятой.
 * Источник: ISO 4217. RUB — 2 (копейки), JPY — 0 (целые йены).
 */
const CURRENCY_SCALE: Record<string, number> = {
  RUB: 2,
  JPY: 0,
  USD: 2,
  EUR: 2,
};

/**
 * formatMinorMoney форматирует сумму в минимальных единицах валюты
 * через registry-driven подход. Для RUB (scale=2) — kopecks → рубли.
 * Используется на presentation boundary (UI, CSV).
 * Для неизвестных валют fallback на RUB-форматирование с decimal numeric.
 */
export function formatMinorMoney(amount: number, currency: string = 'RUB'): string {
  const scale = CURRENCY_SCALE[currency] ?? 2;
  const major = scale === 0 ? amount : amount / Math.pow(10, scale);
  // Intl.NumberFormat падает на невалидных ISO 4217 кодах; fallback на decimal.
  if (!(currency in CURRENCY_SCALE)) {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: scale,
      maximumFractionDigits: scale,
    }).format(major);
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
  }).format(major);
}

/**
 * Форматирует цену в минимальных единицах валюты (для RUB — копейки).
 * Делегирует к registry-driven formatMinorMoney.
 */
export function formatKopecks(kopecks: number): string {
  return formatMinorMoney(kopecks, 'RUB');
}

/**
 * Преобразует пользовательский ввод рублей в минимальные единицы валюты (для RUB — копейки)
 */
export function parseKopecks(value: string): number | undefined {
  const normalized = value.trim().replace(',', '.');
  const match = normalized.match(/^(\d+)(?:\.(\d{0,2}))?$/);
  if (!match) return undefined;

  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(2, '0'));
  const kopecks = whole * 100 + fraction;
  return Number.isSafeInteger(kopecks) ? kopecks : undefined;
}
